import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Facebook, Twitter, Instagram, Youtube, Sprout } from "lucide-react";
import { useSiteSettings } from "@/contexts/SiteSettingsContext";
import { useI18n } from "@/contexts/I18nContext";

const Footer = () => {
  const { settings } = useSiteSettings();
  const { t } = useI18n();
  const wa = settings.whatsapp_number || "918090245875";

  return (
    <footer className="bg-[#0F2A14] text-white/90 mt-20" data-testid="site-footer">
      <div className="container-fah pt-16 pb-10 grid grid-cols-2 md:grid-cols-4 gap-10">
        <div className="col-span-2 md:col-span-1">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center overflow-hidden">
              {settings.logo_url ? <img src={settings.logo_url} alt={settings.site_name} className="w-full h-full object-cover" /> : <Sprout className="w-6 h-6 text-white" />}
            </div>
            <div>
              <div className="font-display font-extrabold text-lg">{settings.site_name}</div>
              <div className="text-[10px] uppercase tracking-wider text-white/60">{t(settings.short_tagline)}</div>
            </div>
          </div>
          <p className="text-sm text-white/70 leading-relaxed">
            {t(settings.tagline)}. {t("India's trusted multi-vendor marketplace for quality agri-inputs.")}
          </p>
          <div className="flex gap-3 mt-5">
            <a href="#" className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center" aria-label="Facebook" data-testid="social-fb"><Facebook className="w-4 h-4" /></a>
            <a href="#" className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center" aria-label="Twitter" data-testid="social-tw"><Twitter className="w-4 h-4" /></a>
            <a href="#" className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center" aria-label="Instagram" data-testid="social-ig"><Instagram className="w-4 h-4" /></a>
            <a href="#" className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center" aria-label="YouTube" data-testid="social-yt"><Youtube className="w-4 h-4" /></a>
          </div>
        </div>

        <div>
          <div className="font-display font-bold mb-4 text-white">{t("Shop By Category")}</div>
          <ul className="space-y-2 text-sm text-white/70">
            <li><Link to="/category/seeds" className="hover:text-white">{t("Seeds")}</Link></li>
            <li><Link to="/category/pesticides" className="hover:text-white">{t("Pesticides")}</Link></li>
            <li><Link to="/category/bio-fertilizers" className="hover:text-white">{t("Bio Fertilizers")}</Link></li>
            <li><Link to="/category/irrigation-products" className="hover:text-white">{t("Irrigation")}</Link></li>
            <li><Link to="/category/farm-machinery" className="hover:text-white">{t("Farm Machinery")}</Link></li>
            <li><Link to="/categories" className="hover:text-white">{t("View All →")}</Link></li>
          </ul>
        </div>

        <div>
          <div className="font-display font-bold mb-4 text-white">{t("Company")}</div>
          <ul className="space-y-2 text-sm text-white/70">
            <li><Link to="/about" className="hover:text-white">{t("About Us")}</Link></li>
            <li><Link to="/seller/register" className="hover:text-white">{t("Become a Seller")}</Link></li>
            <li><Link to="/contact" className="hover:text-white">{t("Contact")}</Link></li>
            <li><Link to="/contact" className="hover:text-white">{t("Returns & Refunds")}</Link></li>
            <li><Link to="/about" className="hover:text-white">{t("Privacy Policy")}</Link></li>
            <li><Link to="/about" className="hover:text-white">{t("Terms of Service")}</Link></li>
          </ul>
        </div>

        <div>
          <div className="font-display font-bold mb-4 text-white">{t("Get In Touch")}</div>
          <ul className="space-y-3 text-sm text-white/70">
            <li className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 text-secondary" /><span>{t(settings.contact_address)}</span></li>
            <li className="flex items-center gap-2"><Phone className="w-4 h-4 text-secondary" /><a href={`tel:+${wa}`} className="hover:text-white">{settings.contact_phone}</a></li>
            <li className="flex items-center gap-2"><Mail className="w-4 h-4 text-secondary" /><a href={`mailto:${settings.contact_email}`} className="hover:text-white">{settings.contact_email}</a></li>
          </ul>
          <a
            href={`https://wa.me/${wa}?text=Hi%2C%20I%20need%20help%20with%20FarmersAgroHub`}
            target="_blank" rel="noreferrer"
            className="mt-4 inline-flex items-center gap-2 bg-[#25D366] hover:opacity-90 text-white font-semibold rounded-lg px-4 py-2 text-sm"
            data-testid="footer-whatsapp-btn"
          >
            <span className="w-5 h-5 inline-flex items-center justify-center bg-white/20 rounded-full">💬</span>
            {t("Chat on WhatsApp")}
          </a>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="container-fah py-5 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/60">
          <div>© {new Date().getFullYear()} {settings.site_name}. {t("All rights reserved.")} <span className="hidden sm:inline">{t("Made with 🌱 in India.")}</span></div>
          <div className="flex items-center gap-4">
            <span>{t("Secured payments")}</span>
            <span>•</span>
            <span>{t("GST verified sellers")}</span>
            <span>•</span>
            <span>{t("Pan-India delivery")}</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
